/**
 * Informed-export flow state machine.
 *
 * Per PFO §7.2 + Tech Spec §6.1:
 *
 *   "Before export of records from the Smart Health Account to
 *    destinations outside PBC-operated infrastructure, PHG presents
 *    patient-comprehensible notice that exported data exits SHT's
 *    fiduciary protections..."
 *
 * The flow walks the patient through:
 *
 *   1. Scope selection — what to export (categories / time range /
 *      operations log / aggregated payload / consent artifacts)
 *   2. Format selection — FHIR Bundle / FHIR AuditEvent Bundle /
 *      structured JSON / original holder format
 *   3. Destination — downloaded file vs. third-party app
 *   4. Informed-export notice — the doctrinal-honest disclosure
 *      surface; what changes when records leave PBC-operated
 *      infrastructure (no longer subject to §7.3 storage architecture;
 *      no longer subject to SHT-mediated consent mechanics or
 *      revocation propagation; no longer integrated with substrate
 *      audit-chain provenance for operations against the export)
 *   5. Erasure composition (optional) — patient may compose this
 *      export with patient-side cryptographic erasure per ADR-024
 *      §2.7 three-outcome election (erase both / holder-retained-
 *      only / PHG-resident-only). Patient affirmatively chooses;
 *      no pre-selected default. ADR-024 §2.7.5: recovery-failure-
 *      as-implicit-erasure is explicitly NOT the architectural
 *      posture, and the UX must keep "I want to erase" distinct
 *      from "I want to leave the substrate without erasing."
 *   6. Confirmation
 *   7. Execute
 *   8. Complete
 *
 * Cancellation reachable from any non-terminal state; error events
 * route to the terminal `error` state.
 *
 * **Doctrinal posture.** The informed-export notice (step 4) is
 * the load-bearing patient-fiduciary disclosure surface for this
 * flow. Step 5's three-outcome erasure election composes against
 * ADR-024 §2.7.2 — patient affirmatively chooses; no pre-selected
 * default — and the UX must surface the distinction between
 * "preserve patient projection" (outcome 2) and "preserve substrate
 * audit chain" (outcome 3) cleanly. Patient-Facing Terms v1.3
 * (coord issue #42) supplies canonical export language when v1.3
 * lands; placeholder copy aligned with PFO §7.2 + ADR-024 §2.7
 * until then.
 */
export const EXPORT_TERMINAL_STATES = new Set([
    'complete',
    'cancelled',
    'error',
]);
export function isTerminal(state) {
    return EXPORT_TERMINAL_STATES.has(state.kind);
}
// ---------- Transitions ----------
export class ExportTransitionError extends Error {
    constructor(currentKind, eventKind) {
        super(`invalid export transition: cannot apply event "${eventKind}" in state "${currentKind}"`);
        this.name = 'ExportTransitionError';
    }
}
export function applyEvent(state, event) {
    if (!isTerminal(state)) {
        if (event.kind === 'cancel') {
            return { kind: 'cancelled', reason: event.reason };
        }
        if (event.kind === 'error') {
            return { kind: 'error', code: event.code, message: event.message };
        }
    }
    switch (state.kind) {
        case 'intro':
            if (event.kind === 'begin-scope-selection') {
                return { kind: 'scope-selection' };
            }
            break;
        case 'scope-selection':
            if (event.kind === 'select-categories') {
                if (event.categories.length === 0) {
                    throw new ExportTransitionError(state.kind, event.kind);
                }
                return { kind: 'format-selection', categories: event.categories };
            }
            break;
        case 'format-selection':
            if (event.kind === 'select-format') {
                return {
                    kind: 'destination-selection',
                    categories: state.categories,
                    format: event.format,
                };
            }
            break;
        case 'destination-selection':
            if (event.kind === 'select-destination') {
                return {
                    kind: 'notice-presentation',
                    selection: {
                        categories: state.categories,
                        format: state.format,
                        destination: event.destination,
                    },
                };
            }
            break;
        case 'notice-presentation':
            if (event.kind === 'acknowledge-notice') {
                return { kind: 'erasure-composition-selection', selection: state.selection };
            }
            break;
        case 'erasure-composition-selection':
            if (event.kind === 'select-erasure-composition') {
                return {
                    kind: 'confirmation',
                    selection: state.selection,
                    erasureComposition: event.composition,
                };
            }
            break;
        case 'confirmation':
            if (event.kind === 'confirm') {
                return {
                    kind: 'executing',
                    selection: state.selection,
                    erasureComposition: state.erasureComposition,
                };
            }
            break;
        case 'executing':
            if (event.kind === 'export-completed') {
                return {
                    kind: 'complete',
                    selection: state.selection,
                    erasureComposition: state.erasureComposition,
                    result: event.result,
                    ...(event.erasureAudit !== undefined ? { erasureAudit: event.erasureAudit } : {}),
                };
            }
            break;
        case 'complete':
        case 'cancelled':
        case 'error':
            break;
    }
    throw new ExportTransitionError(state.kind, event.kind);
}
export function initialExportState() {
    return { kind: 'intro' };
}
//# sourceMappingURL=export-state.js.map